import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TextField,
  Typography,
  Box,
  Paper,
  Divider,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import "../../styles/PrintNOR.css";
import { renderObqRobV2 } from "../../print-v2";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useTranslation } from "react-i18next";
import { getVoyageNumber } from "../../utils/domainSelectors";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";

const ObqRob = () => {
  const { t } = useTranslation("obqrob");
  const { selectedTripId, selectedCargoIndex, currentVoyage } =
    useVoyageContextV2();
  const { companyLogo } = useCompanyContext();
  const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);
  const selectedCargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [cargoData, setCargoData] = useState([
    { tank: "1P", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "2P", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "3P", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "4P", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "5P", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "6P", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "1C", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "2C", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "3C", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "4C", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "5C", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
    { tank: "6C", dip: "", cargo: "", dens: "", volume: "", remarks: "" },
  ]);
  const [isLiquid, setIsLiquid] = useState(false);
  const [isResidues, setIsResidues] = useState(false);
  const [totalVolume, setTotalVolume] = useState(0);
  const [totalMt, setTotalMt] = useState(0);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (currentVoyage) {
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [currentVoyage]);
  useEffect(() => {
    // Only update draft when voyage, trip, or cargo data changes
    const getDraft = () => {
      let fwd = "";
      let aft = "";
      let trim = "";
      if (selectedCargo) {
        if (selectedTrip?.operationType === "Loading") {
          fwd =
            selectedCargo?.activities?.["first-line-ashore"]?.remarks?.forward ||
            "";
          aft =
            selectedCargo?.activities?.["first-line-ashore"]?.remarks?.aft || "";
          trim = Number(fwd) - Number(aft);
        } else {
          fwd =
            selectedCargo?.activities?.["pilot-disembarked"]?.remarks?.forward ||
            "";
          aft =
            selectedCargo?.activities?.["pilot-disembarked"]?.remarks?.aft || "";
          trim = Number(fwd) - Number(aft);
        }
      }
      if (fwd && aft) {
        setDraft({ fwd, aft, trim });
      } else {
        setDraft({});
      }
    };

    getDraft(); // Run the draft logic
  }, [selectedCargo, selectedTrip]);

  const handlePrintButton = () => {
    if (!selectedCargo) {
      alert("Please select a cargo before printing.");
      return;
    }
    renderObqRobV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      companyLogo,
      cargoData,
      isLiquid,
      isResidues,
      totalVolume,
      totalMt,
      draft,
      t,
    });
  };
  const handleFieldChange = (index, field, value) => {
    const updatedCargoData = [...cargoData];
    updatedCargoData[index][field] = value;
    setCargoData(updatedCargoData);
    recalculateTotals(updatedCargoData);
  };

  const recalculateTotals = (updatedCargoData) => {
    let volumeTotal = 0;
    let mtTotal = 0;
    updatedCargoData.forEach((row) => {
      if (row.volume) {
        volumeTotal += parseFloat(row.volume) || 0;
      }
      if (row.dens && row.volume) {
        mtTotal += (parseFloat(row.volume) * parseFloat(row.dens)) / 1000 || 0;
      }
    });
    setTotalVolume(volumeTotal);
    setTotalMt(mtTotal);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: 4,
        }}
      >
        <Typography variant="h4">Loading data</Typography>{" "}
      </Box>
    );
  }

  if (!currentVoyage) {
    return (
      <Typography variant="h4">
        No voyage data available. Start a voyage under Current Voyage
      </Typography>
    );
  }

  return (
    <div>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        {t("title")}
      </Typography>

      <Paper
        sx={{
          padding: 2,
          boxShadow: "4px 4px 10px 2px rgba(0, 0, 0, 0.3)",
        }}
        elevation={3}
      >
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          {t("voyageInfo.title")}
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  {t("voyageInfo.voyage")}
                </TableCell>
                <TableCell>{getVoyageNumber(currentVoyage)}</TableCell>
                <TableCell className="print-nor-table-info-label">
                  {t("voyageInfo.date")}
                </TableCell>
                <TableCell>
                  {selectedTrip?.date}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  {t("voyageInfo.port")}
                </TableCell>
                <TableCell>
                  {selectedTrip?.from}
                </TableCell>
                <TableCell className="print-nor-table-info-label">
                  {t("voyageInfo.jetty")}
                </TableCell>
                <TableCell>
                  {selectedTrip?.jetty || "N/A"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  {t("voyageInfo.condition")}
                </TableCell>
                <TableCell>
                  {selectedTrip?.operationType === "Loading"
                    ? t("voyageInfo.beforeLoading")
                    : t("voyageInfo.afterDischarge")}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="print-nor-table-info-label">
                  {t("voyageInfo.draft")}
                </TableCell>
                <TableCell>
                  {draft.fwd
                    ? `FWD: ${draft.fwd}`
                    : t("voyageInfo.updateInSoF")}
                </TableCell>
                <TableCell>{draft.aft ? `AFT: ${draft.aft}` : ""}</TableCell>
                <TableCell>
                  {draft.trim !== undefined ? `Trim: ${draft.trim}` : ""}
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
        <Paper elevation={3}>
          <TableContainer sx={{ marginTop: 2 }}>
            <Table
              sx={{
                minWidth: 650,
                background: isDarkMode ? "#424242" : "#fff",
              }}
            >
              <TableHead
                sx={{
                  background: isDarkMode ? "#00796b" : "#7AB2D3",
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("table.tank")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("table.dip")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("table.cargo")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("table.dens15c")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("table.volumem3")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    {t("table.remarks")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargoData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.tank}</TableCell>
                    <TableCell>
                      <TextField
                        value={row.dip}
                        onChange={(e) =>
                          handleFieldChange(index, "dip", e.target.value)
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.cargo}
                        onChange={(e) =>
                          handleFieldChange(index, "cargo", e.target.value)
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.dens}
                        onChange={(e) =>
                          handleFieldChange(index, "dens", e.target.value)
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.volume}
                        onChange={(e) =>
                          handleFieldChange(index, "volume", e.target.value)
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={row.remarks}
                        onChange={(e) =>
                          handleFieldChange(index, "remarks", e.target.value)
                        }
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label={t("table.totalVolume")}
            value={totalVolume}
            fullWidth
            disabled
          />
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <TextField
            label={t("table.totalMt")}
            value={totalMt}
            fullWidth
            disabled
          />
        </Box>
        <Box sx={{ marginTop: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isLiquid}
                onChange={(e) => setIsLiquid(e.target.checked)}
              />
            }
            label={t("table.robLiquid")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isResidues}
                onChange={(e) => setIsResidues(e.target.checked)}
              />
            }
            label={t("table.robResidue")}
          />
        </Box>
      </Paper>
      <Divider sx={{ marginY: 2 }} />
      <Button
        variant="contained"
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrintButton}
      >
        {t("print")} {t("title")}
      </Button>
    </div>
  );
};

export default ObqRob;
